/**
 * SelectedMarker — the "you clicked this" ping.
 *
 * History: this used to be a person silhouette standing on the focal
 * node, and before that an orbital-ring + amber-halo treatment. Both
 * were pulled — permanent furniture parked on the node reads as clutter,
 * and the deck's own node grammar says state should be carried by the
 * node itself ("ring / glow = state; never move the node to show state").
 *
 * So selection is now signalled three ways, none of which leave a
 * standing object behind:
 *   1. the node PULSES brighter than its neighbours (Nodes.tsx, hot
 *      enough to clear the Bloom threshold),
 *   2. this SONAR PING — a couple of rings that expand out of the node
 *      and fade, re-firing on a slow interval for as long as the
 *      selection stands,
 *   3. its label goes bright and full-weight (ProximityLabels/HubLabels).
 *
 * The ping is the "where is it?" cue — it fires immediately on selection
 * and then beats slowly, so you can find the node again after looking
 * away. Between beats it draws nothing at all.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { Graph } from '../types/domain';
import { useAppStore } from '../state/store';
import { visualRadius } from './sizing';
import type { ForceLayout } from './useForceLayout';

/** Rings emitted per burst. */
const PING_COUNT = 2;
/**
 * Seconds between bursts. The sonar repeats for as long as something is
 * selected, so the cue survives you looking away, rotating, or zooming
 * out — but with a long enough gap that it reads as a slow beacon rather
 * than a constant animation.
 */
const PING_INTERVAL = 3.6;
/** Seconds for one ring to travel from the node to its full extent. */
const PING_DURATION = 0.9;
/** Gap between successive rings. */
const PING_STAGGER = 0.28;
/** Ring's final outer radius, in node radii. */
const PING_MAX_SCALE = 3.4;
/** Inner/outer ratio — thickness of the ring. */
const PING_INNER = 0.82;
const PING_COLOR = '#dff0f7';
const PING_OPACITY = 0.55;

export function SelectedMarker({
  graph,
  layout,
  focalId,
  centerId,
}: {
  graph: Graph;
  layout: ForceLayout;
  focalId: string | null;
  /** The protagonist node — CenterAnchor draws it at a fixed radius, so
   *  the ping must size against that, not nodeRadius(). */
  centerId?: string;
}) {
  // The ping sizes itself against the node's sphere, so it has to read the
  // same sizing mode Nodes.tsx draws with or the ring floats off the node.
  const sizeBy = useAppStore((s) => s.sizeBy);
  const groupRef = useRef<THREE.Group>(null!);
  const ringRefs = useRef<(THREE.Mesh | null)[]>([]);
  // Wall-clock start of the CURRENT SELECTION. Bursts repeat every
  // PING_INTERVAL from here; null when nothing is selected.
  const startRef = useRef<number | null>(null);
  const rings = useMemo(() => Array.from({ length: PING_COUNT }), []);
  // Billboard orientation, refreshed each frame from the camera.
  const cameraQuat = useMemo(() => new THREE.Quaternion(), []);

  // Re-arm on every selection CHANGE (including re-selecting nothing →
  // something). This is the whole trigger: no selection change, no ping.
  useEffect(() => {
    startRef.current = focalId ? performance.now() / 1000 : null;
  }, [focalId]);

  useFrame(({ camera }) => {
    const g = groupRef.current;
    if (!g) return;
    cameraQuat.copy(camera.quaternion);
    const pos = layout.posRef.current;
    const idx = focalId ? layout.nodeIndex.get(focalId) : undefined;
    const node = focalId ? graph.byId.get(focalId) : undefined;
    const start = startRef.current;

    if (
      !focalId ||
      !pos ||
      idx === undefined ||
      !node ||
      start === null ||
      idx * 3 + 2 >= pos.length
    ) {
      g.visible = false;
      return;
    }

    const total = PING_DURATION + PING_STAGGER * (PING_COUNT - 1);
    // Phase within the current cycle — the burst plays, then the rings
    // rest until the next interval comes round.
    const elapsed = (performance.now() / 1000 - start) % PING_INTERVAL;
    if (elapsed > total) {
      // Resting between bursts. Keep the group alive (the next cycle is
      // coming) but draw nothing.
      for (const m of ringRefs.current) if (m) m.visible = false;
      g.visible = false;
      return;
    }
    g.visible = true;
    g.position.set(pos[idx * 3], pos[idx * 3 + 1], pos[idx * 3 + 2]);
    // Billboard so each ring reads as a clean circle from any angle.
    g.quaternion.copy(cameraQuat);

    const r = visualRadius(node, focalId === centerId, sizeBy);
    for (let i = 0; i < PING_COUNT; i++) {
      const mesh = ringRefs.current[i];
      if (!mesh) continue;
      const t = (elapsed - i * PING_STAGGER) / PING_DURATION;
      if (t < 0 || t > 1) {
        mesh.visible = false;
        continue;
      }
      mesh.visible = true;
      // Ease-out so it leaps away from the node then settles.
      const eased = 1 - Math.pow(1 - t, 2.2);
      const s = r * (1 + (PING_MAX_SCALE - 1) * eased);
      mesh.scale.set(s, s, s);
      const mat = mesh.material as THREE.MeshBasicMaterial;
      // Fade out as it expands — the ring dissolves rather than stopping.
      mat.opacity = PING_OPACITY * (1 - t) * (1 - t);
    }
  });


  if (!focalId) return null;

  return (
    <group ref={groupRef} renderOrder={9}>
      {rings.map((_, i) => (
        <mesh
          key={i}
          ref={(el) => {
            ringRefs.current[i] = el;
          }}
          raycast={() => null}
        >
          <ringGeometry args={[PING_INNER, 1, 48]} />
          <meshBasicMaterial
            color={PING_COLOR}
            transparent
            opacity={0}
            side={THREE.DoubleSide}
            depthTest={false}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>
      ))}
    </group>
  );
}
